import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FaUndo, FaFilter } from 'react-icons/fa';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import CheckboxDropdownMenu from '@/shared/components/input/CheckboxDropdownMenu';
import userStorage from '@/shared/services/userStorage';

import { Dropdown } from 'react-bootstrap';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import MultiSelect, {
  addItem as addMultiSelectItem,
  removeItem as removeMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import dateMessages from '@/shared/messages/dateMessages';
import RelationSearch from '@/shared/components/input/RelationSearch';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import TextSearch from '@/shared/components/input/TextSearch';

import { dynamicSort } from '@/shared/utils/dynamicSort';
import enumCache from '@/shared/services/EnumCacheService';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import HasMultiCompany from '@/shared/components/security/HasMultiCompany';
import UserSearch from '@/shared/components/input/UserSearch';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  initDynamicFilters,
} from '@/shared/components/DynamicFilter';
import messages from './documentMessages';
import config from '../../config';
import { FilterSave } from '@/shared/components/FilterSave';

const dynamicFiltersStorageKey = 'EmailOverviewDynamicFilters';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  companyId: null,
  dateUntil: null,
  dateFrom: null,
  period: 'all',
  documentTypes: [],
  recognitionStatus: [],
  subject: '',
  sender: '',
  recipients: '',
  notLinkedToRelation: false,
  linkedToMultipleRelations: false,
  ownCompanies: [],
  emailStatus: [],
  createdBy: null,
  withTags: [],
  withoutTags: [],
  emailLinkTypes: [],
  responsible: null,
  assistant: null,
  assistantBackup: null,
  associate: null,
  relationGroups: [],
};

const allDynamicFilters = [...getDynamicFiltersFromDefaultFilters(defaultFilters, 'ownCompanies')];

class EmailFilters extends Component {
  state = {
    dynamicFilters: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      relationId: PropTypes.string,
      relationName: PropTypes.string,
      companyId: PropTypes.string,
      dateUntil: momentPropTypes.momentObj,
      dateFrom: momentPropTypes.momentObj,
      period: PropTypes.oneOf([
        'all',
        'month',
        'quarter',
        'year',
        'year-1',
        'year-2',
        'year-3',
        'year-4',
        'year-5',
      ]),
      subject: PropTypes.string,
      sender: PropTypes.string,
      recipients: PropTypes.string,
      notLinkedToRelation: PropTypes.bool,
      linkedToMultipleRelations: PropTypes.bool,
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      emailStatus: PropTypes.arrayOf(PropTypes.number),
      withTags: PropTypes.arrayOf(PropTypes.string),
      withoutTags: PropTypes.arrayOf(PropTypes.string),
      emailLinkTypes: PropTypes.arrayOf(PropTypes.string),
      createdBy: PropTypes.string,
      relationGroups: PropTypes.arrayOf(PropTypes.string),
    }),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: {
      ...defaultFilters,
    },
  };

  componentDidMount() {
    const { filters, intl } = this.props;

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    Promise.all([
      enumCache.get('combinedEmailStatus'),
      api.base.get(`${config.appUrls.documents}/api/Tag`),
    ]).then(([emailStatusses, { data: tags }]) => {
      this.setState({
        emailStatusses: emailStatusses.map(item => ({
          id: item.key,
          label: intl.formatMessage(listItemMsg('combinedEmailStatus', item.key, item.value)),
        })),
        tags: (tags || []).map(x => ({ ...x, label: x.name })).sort(dynamicSort('label')),
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };

  resetFilters = () => {
    const { onFilterChange } = this.props;
    onFilterChange({ ...defaultFilters });
  };

  setFilters = filters => {
    if (!!filters.dateFrom) filters.dateFrom = moment(filters.dateFrom);
    if (!!filters.dateUntil) filters.dateUntil = moment(filters.dateUntil);
    this.props.onFilterChange(filters);
  };

  dateChanged = (startDate, endDate) => {
    this.filterChanged({ dateFrom: startDate, dateUntil: endDate, period: null });
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null, companyId: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
        companyId: item.companyId,
      });
    }
  };

  dynamicFilterChanged = (item, selected, visibleItemIds) => {
    const { dynamicFilters } = this.state;
    const { filters } = this.props;

    const newFilters = dynamicFilters.map(x => {
      if (x.id !== item.id && item.id !== 'selectall') return x;
      if (visibleItemIds && !visibleItemIds.includes(x.id)) return x;
      return { ...x, selected };
    });

    userStorage.setItem(
      dynamicFiltersStorageKey,
      newFilters.filter(x => x.selected).map(x => x.id),
    );
    this.setState({
      dynamicFilters: newFilters,
    });

    if (!selected && JSON.stringify(filters[item.id]) !== JSON.stringify(defaultFilters[item.id])) {
      // Reset filter when it's removed
      this.filterChanged({ [item.id]: defaultFilters[item.id] });
    }
  };

  toggleNotLinkedtoRelation = () => {
    const { filters } = this.props;
    this.filterChanged({
      notLinkedToRelation: !filters.notLinkedToRelation,
      linkedToMultipleRelations: false,
    });
  };
  toggleLinkedToMultipleRelations = () => {
    const { filters } = this.props;
    this.filterChanged({
      linkedToMultipleRelations: !filters.linkedToMultipleRelations,
      notLinkedToRelation: false,
    });
  };

  setPeriod = period => {
    let dateFrom = null;
    let dateUntil = null;
    switch (period) {
      case 'all':
      case '':
      case null:
      case undefined:
        break;
      case 'month':
      case 'quarter':
      case 'year':
        dateFrom = moment.utc().startOf(period);
        dateUntil = moment.utc().endOf(period);
        break;
      case 'year-1':
      case 'year-2':
      case 'year-3':
      case 'year-4':
      case 'year-5': {
        const y = parseInt(period.substr(-1, 1));
        dateFrom = moment
          .utc()
          .startOf('year')
          .subtract(y, 'y');
        dateUntil = moment
          .utc()
          .endOf('year')
          .subtract(y, 'y');
        break;
      }
      default:
        console.debug(`Unknown period: ${period}`);
        return;
    }

    this.filterChanged({ dateFrom, dateUntil, period });
  };

  onEmailStatusFilterAdded = selectedItem => {
    if (!selectedItem) return;

    const { filters } = this.props;
    const { emailStatus = [] } = filters;

    if (emailStatus.includes(selectedItem.id)) return;
    this.filterChanged({
      emailStatus: [...emailStatus, selectedItem.id],
    });
  };
  onEmailStatusFilterRemoved = item => {
    const { filters } = this.props;
    const { emailStatus = [] } = filters;
    const index = emailStatus.findIndex(x => x === item.value.id);
    const copy = [...emailStatus];
    copy.splice(index, 1);

    this.filterChanged({ emailStatus: copy });
  };

  onCreatedByChanged = user => {
    this.filterChanged({
      createdBy: user ? user.id : user,
    });
  };

  render() {
    const { emailStatusses, tags, dynamicFilters } = this.state;
    const { filters, intl, selectionCount } = this.props;
    const defaultBtnClass = 'btn btn-default';

    let initRelation = null;
    if (filters.relationId) {
      initRelation = {
        key: filters.relationId,
        label: filters.relationName,
        invoiceStreamId: filters.invoiceStreamId,
        companyId: filters.companyId,
      };
    }
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    const dropdownFilters = [
      {
        id: 'selectall',
        label: intl.formatMessage(messages.SelectAll),
        selected: !dynamicFilters.some(x => !x.selected),
      },
      ...dynamicFilters,
    ];

    const selectedEmailStatusses =
      emailStatusses && filters.emailStatus
        ? emailStatusses.filter(r => filters.emailStatus.includes(r.id))
        : [];

    const [withTagsSelection, withTagsAvailable] = splitMultiSelectItems(tags, filters.withTags);
    const [withoutTagsSelection, withoutTagsAvailable] = splitMultiSelectItems(
      tags,
      filters.withoutTags,
    );

    const year = new Date().getFullYear();

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);

    return (
      <>
        <div className="row">
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <RelationSearch
                onChange={this.relationChanged}
                initialSelectedItem={initRelation}
                changeOnInit
              />
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.dateFrom}
                startDateId="DocumentFilterDateFrom"
                endDate={filters.dateUntil}
                endDateId="DocumentFilterDateUntil"
                onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
                block
              />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group">
              <div role="group" aria-label="period" className="btn-group btn-group-justified">
                <button
                  onClick={() => this.setPeriod('month')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'month' })}
                >
                  <FormattedMessage {...dateMessages.ThisMonth} />
                </button>
                <button
                  onClick={() => this.setPeriod('quarter')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'quarter' })}
                >
                  <FormattedMessage {...dateMessages.ThisQuarter} />
                </button>
                <button
                  onClick={() => this.setPeriod('year')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'year' })}
                >
                  {year}
                </button>
                <button
                  onClick={() => this.setPeriod('year-1')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'year-1' })}
                >
                  {year - 1}
                </button>
                <button
                  onClick={() => this.setPeriod('year-2')}
                  type="button"
                  className={classnames(defaultBtnClass, { active: filters.period === 'year-2' })}
                >
                  {year - 2}
                </button>

                <button
                  onClick={() => this.setPeriod('all')}
                  type="button"
                  className={classnames(defaultBtnClass, {
                    active: !filters.dateFrom && !filters.dateUntil,
                  })}
                >
                  <FormattedMessage {...dateMessages.All} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <TextSearch
                className="form-control"
                value={filters.subject}
                placeholder={intl.formatMessage(messages.Subject)}
                onChange={value => this.filterChanged({ subject: value })}
              />
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <UserSearch
                placeholder={intl.formatMessage(messages.DocumentCreatedBy)}
                value={filters.createdBy}
                onChange={this.onCreatedByChanged}
              />
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <div role="group" className="btn-group btn-group-justified">
                <button
                  onClick={() => {
                    document.activeElement.blur();
                    this.toggleNotLinkedtoRelation();
                  }}
                  type="button"
                  title={intl.formatMessage(messages.NotLinkedToARelation)}
                  className={classnames('btn btn-default', {
                    active: filters.notLinkedToRelation,
                  })}
                >
                  {intl.formatMessage(messages.NotLinkedToARelationShort)}
                </button>

                <button
                  onClick={() => {
                    document.activeElement.blur();
                    this.toggleLinkedToMultipleRelations();
                  }}
                  type="button"
                  title={intl.formatMessage(messages.LinkedToMultipleRelations)}
                  className={classnames('btn btn-default', {
                    active: filters.linkedToMultipleRelations,
                  })}
                >
                  {intl.formatMessage(messages.LinkedToMultipleRelationsShort)}
                </button>
              </div>
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <EnumMultiSelect
                type="EmailLinkType"
                selection={filters.emailLinkTypes}
                orderByLabel
                onChange={value => this.filterChanged({ emailLinkTypes: value })}
                placeholder={intl.formatMessage(messages.EmailLinkType)}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <TextSearch
                className="form-control"
                value={filters.sender}
                placeholder={intl.formatMessage(messages.From)}
                onChange={value => this.filterChanged({ sender: value })}
              />
            </div>
          </div>
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <TextSearch
                className="form-control"
                value={filters.recipients}
                placeholder={intl.formatMessage(messages.ToCcBcc)}
                onChange={value => this.filterChanged({ recipients: value })}
              />
            </div>
          </div>
          {tags && tags.length > 0 ? (
            <Fragment>
              <div className="col-md-6 col-lg-3">
                <div className="form-group">
                  <MultiSelect
                    selectedItem={withTagsSelection}
                    onItemRemoved={item =>
                      this.filterChanged({
                        withTags: removeMultiSelectItem(filters.withTags, item),
                      })
                    }
                    onItemSelected={item =>
                      this.filterChanged({
                        withTags: addMultiSelectItem(filters.withTags, item),
                      })
                    }
                    items={withTagsAvailable}
                    placeholder={intl.formatMessage(messages.WithDocumentTags)}
                  />
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="form-group">
                  <MultiSelect
                    selectedItem={withoutTagsSelection}
                    onItemRemoved={item =>
                      this.filterChanged({
                        withoutTags: removeMultiSelectItem(filters.withoutTags, item),
                      })
                    }
                    onItemSelected={item =>
                      this.filterChanged({
                        withoutTags: addMultiSelectItem(filters.withoutTags, item),
                      })
                    }
                    items={withoutTagsAvailable}
                    placeholder={intl.formatMessage(messages.WithoutDocumentTags)}
                  />
                </div>
              </div>
            </Fragment>
          ) : null}
        </div>
        <div className="row">
          <div className="col-md-6 col-lg-3">
            <div className="form-group">
              <MultiSelect
                selectedItem={selectedEmailStatusses}
                onItemRemoved={this.onEmailStatusFilterRemoved}
                onItemSelected={this.onEmailStatusFilterAdded}
                items={emailStatusses}
                placeholder={intl.formatMessage(messages.EmailStatus)}
              />
            </div>
          </div>
          <HasMultiCompany>
            <div className="col-md-6 col-lg-3">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies || []}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            </div>
          </HasMultiCompany>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row hidden-xs hidden-sm">
            {visibleFilters.map(id => (
              <DynamicFilter
                key={id}
                type={id}
                intl={intl}
                filters={filters}
                collClass="col-md-6 col-lg-3"
                filterChanged={this.filterChanged}
              />
            ))}
          </div>
        ) : null}
        <div className="flex-row">
          <div className="btn-group save-filters">
            <FilterSave
              hasActiveFilters={hasFilters}
              screen="emails"
              filtersObject={filters}
              resetFilters={() => this.resetFilters()}
              setFilter={filter => this.setFilters(filter)}
            />
            <Dropdown id="dynamicFilterMenu">
              <Dropdown.Toggle title={intl.formatMessage(messages.SelectYourFilters)}>
                <FaFilter className="react-icon margin5R" />
                <FormattedMessage {...messages.MoreFilters} />
              </Dropdown.Toggle>
              <CheckboxDropdownMenu
                bsRole="menu"
                items={dropdownFilters}
                onItemChanged={this.dynamicFilterChanged}
              />
            </Dropdown>
            {hasFilters && (
              <button
                type="button"
                className="btn btn-default btn-reset margin10L"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
          </div>
          <div className="margin10T" style={{ marginLeft: 'auto' }}>
            {selectionCount}
          </div>
        </div>
      </>
    );
  }
}

export default injectIntl(EmailFilters);
